import {
  ActionIcon,
  Center,
  Group,
  Loader,
  Modal,
  Pagination,
  Stack,
  Table,
  Text,
  Title,
  Tooltip,
} from "@mantine/core";

import { notifications } from "@mantine/notifications";
import { IconDownload, IconEyeSearch } from "@tabler/icons-react";
import { safeApiCall } from "api/common.service";
import { logError } from "api/diagnostic.service";
import { getInvoiceById, getInvoices } from "api/invoice.service";
import {
  MantineNotificationColor,
  MantineNotificationPosition,
  MSG_DEFAULT_FORM_DATA_LOAD_ERROR,
  NAVIGATE_ORDER,
} from "common/constants";
import { navigateTo } from "common/navigation";
import { formatDate, formatNZD } from "common/utils";
import { DEFAULT_PAGE_SIZE } from "components/layouts/consts";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { InvoiceExportSummary, PaginatedInvoices } from "types/model.types";
import AdminExportInvoicePage from "./AdminExportInvoicePage";
import AdminInvoicePreview from "./AdminInvoicePreview";

const AdminInvoicePage: React.FC = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const id = params.get("id");
  const [invoiceId, setInvoiceId] = useState<number | null>(null);
  const [preview, setPreview] = useState(false);
  const [entries, setEntries] = useState<PaginatedInvoices>({
    data: [],
    total: 0,
    pageNumber: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [reloadTrigger, setReloadTrigger] = useState(new Date());
  const [loading, setLoading] = useState<boolean>(true);
  const [activePage, setActivePage] = useState(1);
  const [exportData, setExportData] = useState<InvoiceExportSummary | null>(
    null,
  );

  const handlePrint = (orderId: string | number) => {
    navigateTo(`${NAVIGATE_ORDER}?id=${orderId}&s=${2}&e=0`);
  };
  const handleExport = async (journalId: string | number) => {
    const invoiceId = Number(journalId);
    setInvoiceId(invoiceId);
    const data = await safeApiCall(getInvoiceById(invoiceId));
    setExportData(data);
  };

  useEffect(() => {
    let mounted = true;
    setLoading(true);

    const fetchData = async () => {
      try {
        const response = await safeApiCall(
          getInvoices(activePage, DEFAULT_PAGE_SIZE),
        );
        if (mounted) {
          setEntries(response ?? []);
        }
      } catch (error) {
        logError(error);
        setLoading(false);
        notifications.show({
          message: MSG_DEFAULT_FORM_DATA_LOAD_ERROR,
          position: MantineNotificationPosition.TopCenter,
          color: MantineNotificationColor.Error,
        });
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };
    fetchData();

    return () => {
      mounted = false;
    };
  }, [activePage, reloadTrigger]);

  useEffect(() => {
    if (id) {
      const invoiceId = Number(id);
      handlePrint(invoiceId);
    }
  }, [id]);

  const rows = entries.data.map((entry) => {
    return (
      <Table.Tr key={entry.id}>
        <Table.Td className="action-cell">
          <Group gap="xs" wrap="nowrap">
            <Tooltip label="Print">
              <ActionIcon
                variant="subtle"
                onClick={() => handlePrint(entry.orderId)}
              >
                <IconEyeSearch size={18} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label="Export">
              <ActionIcon
                variant="subtle"
                onClick={() => handleExport(entry.id)}
              >
                <IconDownload size={18} />
              </ActionIcon>
            </Tooltip>
          </Group>
        </Table.Td>
        <Table.Td>{entry.id}</Table.Td>
        <Table.Td>{entry.orderId}</Table.Td>
        <Table.Td>{formatDate(entry.createdOn)}</Table.Td>
        <Table.Td>{entry.debtorName}</Table.Td>
        <Table.Td className="money">{formatNZD(entry.amount)}</Table.Td>
        <Table.Td className="money">{formatNZD(entry.gst)}</Table.Td>
        <Table.Td className="notes">{entry.notes}</Table.Td>
      </Table.Tr>
    );
  });

  return (
    <Stack>
      <Title order={2}>Invoices</Title>
      {loading ? (
        <Center>
          <Loader />
        </Center>
      ) : (
        <>
          <Group justify="flex-end" wrap="nowrap" mb="md">
            <Text size="sm" c="dimmed">
              Showing 30 items per page
            </Text>
            <Pagination
              total={Math.ceil(entries.total / DEFAULT_PAGE_SIZE)}
              value={activePage}
              onChange={setActivePage}
              siblings={1}
              boundaries={1}
            />
          </Group>
          <Table striped highlightOnHover withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ whiteSpace: "nowrap" }}>Actions</Table.Th>
                <Table.Th>Invoice #</Table.Th>
                <Table.Th>Order #</Table.Th>
                <Table.Th>Date</Table.Th>
                <Table.Th>Customer</Table.Th>
                <Table.Th className="money">Amount</Table.Th>
                <Table.Th className="money">GST</Table.Th>
                <Table.Th style={{ minWidth: 300 }}>Notes</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>{rows}</Table.Tbody>
          </Table>
          <Group justify="flex-end" wrap="nowrap" mb="md">
            <Pagination
              total={Math.ceil(entries.total / DEFAULT_PAGE_SIZE)}
              value={activePage}
              onChange={setActivePage}
              siblings={1}
              boundaries={1}
            />
          </Group>
        </>
      )}
      <Modal
        opened={preview}
        onClose={() => {
          setPreview(false);
          setInvoiceId(null);
          navigate(location.pathname, { replace: true });
        }}
        fullScreen
        title="Invoice Preview"
        size="xl"
        centered
        withinPortal={true} // default, but explicit here
        zIndex={2001} // ensure it's above layout elements
      >
        {invoiceId && <AdminInvoicePreview id={invoiceId} />}
      </Modal>
      {exportData && (
        <AdminExportInvoicePage
          data={exportData}
          onDownloadComplete={() => setExportData(null)}
        />
      )}
    </Stack>
  );
};

export default AdminInvoicePage;
